import type { AlchemyType } from '@/domain/genetics/types';
import type { BiomeId, ItemType } from './types';
import { BIOMES } from './biomes';

export type AfkStatus = 'active' | 'claimable' | 'claimed';
export type AfkDuration = 'short' | 'medium' | 'long';

export const AFK_DURATIONS_MS: Record<AfkDuration, number> = {
  short: 1 * 60 * 60_000,
  medium: 4 * 60 * 60_000,
  long: 12 * 60 * 60_000,
};

export interface AfkReward {
  items: Partial<Record<ItemType, number>>;
  xp: number;
}

export interface AfkDispatch {
  id: string;
  npcId: string;
  biome: BiomeId;
  startedAtMs: number;
  durationMs: number;
  status: AfkStatus;
  rewards?: AfkReward;
}

const ITEMS_PER_BIOME: Record<BiomeId, ItemType[]> = {
  meadow: ['berry', 'flower'],
  forest: ['berry', 'flower', 'crystal'],
  cave: ['crystal', 'shell'],
  coast: ['shell', 'flower'],
};

const BASE_ITEMS_PER_HOUR = 1;
const BASE_XP_PER_HOUR = 5;

export function computeRewards(input: {
  biome: BiomeId;
  durationMs: number;
  npcType: AlchemyType | null;
  rng?: () => number;
}): AfkReward {
  const rng = input.rng ?? Math.random;
  const hours = input.durationMs / 3_600_000;
  let mult = 1.0;
  if (input.npcType) {
    const exposure = BIOMES[input.biome].exposure[input.npcType];
    if (exposure < 1) mult = 1.3;       // bioma resistente para o NPC = bônus
    else if (exposure > 1.1) mult = 0.7; // bioma hostil = penalidade
  }
  const itemsCount = Math.max(0, Math.round(hours * BASE_ITEMS_PER_HOUR * mult));
  const xp = Math.max(0, Math.round(hours * BASE_XP_PER_HOUR * mult));
  const itemPool = ITEMS_PER_BIOME[input.biome];
  const items: Partial<Record<ItemType, number>> = {};
  for (let i = 0; i < itemsCount; i++) {
    const t = itemPool[Math.floor(rng() * itemPool.length)]!;
    items[t] = (items[t] ?? 0) + 1;
  }
  return { items, xp };
}

export function isReady(d: AfkDispatch, nowMs: number): boolean {
  return d.status === 'active' && nowMs >= d.startedAtMs + d.durationMs;
}
